/**
 * "How this works for you" — a per-role guide: what a persona can do, where
 * they sit in the real approval chain, who they report to, and where to
 * find things. Content is grounded in the actual role model
 * (server/compliance/rolepacks/zeus-cascade-1.0.0.js, server/helpers/
 * approvalRoles.js's ACTION_PERMISSIONS, server/helpers/spendRequest.js's
 * state machine) and the app's own already-shipped governance copy
 * (client/src/screens/stratus/AccessView.tsx), not invented filler.
 *
 * Nav data is sourced from shell/navSections.ts specifically (not from
 * AppShell.tsx/WorkspaceShell.tsx/ReporterShell.tsx directly) to avoid a
 * circular import: RoleGuideProvider.tsx is used by Sidebar.tsx and
 * ReporterShell.tsx, so if this file imported nav arrays from those shells
 * instead, the graph would loop back on itself.
 */

import type { LucideIcon } from "lucide-react";
import type { Profile } from "./types";
import { stratusNav, funderNav, reporterNav, workspaceNavSections } from "@/shell/navSections";
import type { NavSection, NavItem } from "@/shell/Sidebar";

export type GuideKey = "oversight" | "funder" | "prime_lead" | "sub_lead" | "contractor_member" | "reporter";

export interface GuideChainStep {
  key: string;
  label: string;
}

export interface GuideContent {
  /** Eyebrow shown above the drawer title - the persona's own role label. */
  roleLabel: string;
  /** The cold-open framing statement (NarrativeCard body). */
  framing: string;
  /** The real approval chain, this persona's own step(s) marked via currentStepIndex. */
  chainSteps: GuideChainStep[];
  currentStepIndex: number;
  capabilities: string[];
  reportingLine: string;
}

/**
 * Six variants, not seven: prime_member/sub_member collapse into one
 * contractor_member bucket (the difference is two words of copy, not a
 * difference in nav or chain position); oversight/funder stay single
 * variants (permission nuances within them are already covered by
 * AccessView.tsx's capability matrix, not this guide's job to repeat).
 */
export const ROLE_GUIDE_CONTENT: Record<GuideKey, GuideContent> = {
  oversight: {
    roleLabel: "Oversight",
    framing:
      "You see the whole programme — approved, draft, and everything in flight. Two things only you can do: sign off on submitted evidence, and pass a confirmed milestone on to the funder.",
    chainSteps: [
      { key: "raise", label: "Raised" },
      { key: "approve", label: "Approved" },
      { key: "spend", label: "Committed & spent" },
      { key: "evidence", label: "Evidence submitted" },
      { key: "confirm", label: "You confirm" },
      { key: "route", label: "You route to funder" },
      { key: "release", label: "Funder releases" },
    ],
    currentStepIndex: 4,
    capabilities: [
      "Review and confirm submitted evidence — the separate check that comes after the lead's own approval",
      "Route a confirmed milestone's disbursement to the funder",
      "Manage contractor onboarding — approve or reject a new organisation",
      "Investigate leaks and exceptions across every project, under Signals",
      "See the full audit trail, unrestricted",
    ],
    reportingLine: "You're the top of the cascade inside the platform. The Danish Impact Fund only ever sees what you route to them — never the underlying spend requests.",
  },
  funder: {
    roleLabel: "Funder",
    framing:
      "You never see the underlying spend requests contractors raise — only a milestone that's already been reviewed, confirmed, and routed to you by Stratus. Your decision is the final gate before money moves.",
    chainSteps: [
      { key: "upstream", label: "Handled upstream" },
      { key: "routed", label: "Routed to you" },
      { key: "release", label: "You release" },
    ],
    currentStepIndex: 2,
    capabilities: [
      "Approve or query a routed milestone disbursement",
      "Comment back to Stratus on a routed item",
      "View the full programme summary, approved disbursements, and audit log — read-only",
    ],
    reportingLine: "You report to no one inside the platform — you're the final approver. Large disbursements need two of your team's sign-offs before release (multi-signature).",
  },
  prime_lead: {
    roleLabel: "Prime contractor lead",
    framing: "Your team raises spend asks; you approve them before any money moves.",
    chainSteps: [
      { key: "raise", label: "Team raises" },
      { key: "approve", label: "You approve" },
      { key: "spend", label: "Commit & spend" },
      { key: "evidence", label: "Evidence submitted" },
      { key: "confirm", label: "Stratus confirms" },
      { key: "route", label: "Routed to funder" },
      { key: "release", label: "Funder releases" },
    ],
    currentStepIndex: 1,
    capabilities: [
      "Approve or reject a spend ask raised by your team",
      "Invite prime team members and sub-contractor leads",
      "Set your team's spend threshold",
      "Submit evidence yourself when you raise your own ask",
    ],
    reportingLine: "You report to Stratus oversight. Your prime team, and any sub-contractor leads you invite, report to you. You can never approve an ask you raised yourself — segregation of duties.",
  },
  sub_lead: {
    roleLabel: "Sub-contractor lead",
    framing: "Your sub-project team raises spend asks; you approve them for your sub-project only — never the parent project or other sub-projects.",
    chainSteps: [
      { key: "raise", label: "Your team raises" },
      { key: "approve", label: "You approve (scoped)" },
      { key: "spend", label: "Commit & spend" },
      { key: "evidence", label: "Evidence submitted" },
      { key: "confirm", label: "Stratus confirms" },
      { key: "route", label: "Routed to funder" },
      { key: "release", label: "Funder releases" },
    ],
    currentStepIndex: 1,
    capabilities: [
      "Approve or reject a spend ask raised by your sub-project team",
      "Invite sub-project members and field reporters",
      "Set your sub-project's spend threshold",
      "Submit evidence yourself when you raise your own ask",
    ],
    reportingLine: "You report to your prime contractor lead, and above them, Stratus oversight. Your authority never reaches the parent project or a sibling sub-project.",
  },
  contractor_member: {
    roleLabel: "Team member",
    framing: "You raise spend asks; your lead approves them before you spend.",
    chainSteps: [
      { key: "raise", label: "You raise" },
      { key: "approve", label: "Lead approves" },
      { key: "spend", label: "Commit & spend" },
      { key: "evidence", label: "You submit evidence" },
      { key: "confirm", label: "Stratus confirms" },
      { key: "route", label: "Routed to funder" },
      { key: "release", label: "Funder releases" },
    ],
    currentStepIndex: 0,
    capabilities: [
      "Raise a spend ask with a description, the supplier you're paying, and supporting documents",
      "Submit evidence (a note plus a photo or document) once your ask is approved and you've spent",
    ],
    reportingLine: "You report to your team's lead — the person who approves what you raise.",
  },
  reporter: {
    roleLabel: "Field reporter",
    framing: "You're the person on site. Raise a spend ask, spend once it's approved, then submit evidence — offline captures queue automatically and sync when you're back online.",
    chainSteps: [
      { key: "raise", label: "You raise" },
      { key: "approve", label: "Lead approves" },
      { key: "spend", label: "Commit & spend" },
      { key: "evidence", label: "You submit evidence" },
      { key: "confirm", label: "Stratus confirms" },
      { key: "route", label: "Routed to funder" },
      { key: "release", label: "Funder releases" },
    ],
    currentStepIndex: 0,
    capabilities: [
      "Raise a spend ask for your assigned project or sub-project",
      "Submit evidence — a note plus a photo or document — after you've spent",
      "Work fully offline: a raise or an evidence submission queues on your device and sends automatically once you're back online",
    ],
    reportingLine: "You report to your contractor or sub-contractor lead — the person who approves what you raise.",
  },
};

/** Which of the six content variants applies to this profile, or null when
 * there's nothing accurate to show yet (a profile with no assigned role). */
export function resolveGuideKey(profile: Profile): GuideKey | null {
  if (profile.group === "onboarding") return null;
  if (profile.group === "oversight" || profile.group === "other") return "oversight";
  if (profile.group === "funder") return "funder";
  if (profile.group === "reporter") return "reporter";
  if (profile.group === "contractor") {
    const perms = profile.permissions ?? [];
    // Mirrors hooks.ts's demoAppointableRoles() branching exactly - the
    // established way this codebase distinguishes a lead from a member
    // synchronously from the active profile, without a per-project
    // membership lookup.
    if (perms.includes("contractor.lead")) return "prime_lead";
    if (perms.includes("subcontractor.lead")) return "sub_lead";
    return "contractor_member";
  }
  return null;
}

function flattenNavSections(sections: NavSection[]): NavItem[] {
  return sections.flatMap((s) => s.items);
}

/** Real nav destinations for a guide variant - sourced from the same arrays
 * the actual sidebar/nav renders from, so this can never drift out of sync.
 * Contractor variants need the active workspace's real base path, which
 * isn't known statically (see navSections.ts's workspaceNavSections). */
export function navItemsForGuideKey(key: GuideKey, workspaceBase: string): { to: string; label: string; icon: LucideIcon; description?: string }[] {
  switch (key) {
    case "oversight":
      return flattenNavSections(stratusNav);
    case "funder":
      return flattenNavSections(funderNav);
    case "reporter":
      return reporterNav;
    case "prime_lead":
    case "sub_lead":
    case "contractor_member":
      return flattenNavSections(workspaceNavSections(workspaceBase, true));
  }
}

// --- Dismissal persistence -------------------------------------------------
// Two independent flags, both keyed per profile id (not one global flag)
// since the demo profile switcher lets one browser session walk multiple
// personas - each gets its own first-open and its own dismissal choice.
//
//  - "seen this session" (sessionStorage): the guide has already opened
//    once (auto or manual) since the tab loaded, so it doesn't reopen a
//    second time on every render. This resets every new session/tab -
//    that's deliberate, not a bug: it's what lets "dismissed forever" below
//    actually mean something. If "seen" were persisted forever too (the
//    original design), it alone would already block every future auto-open
//    permanently, making the dismiss checkbox a no-op that looked like it
//    did something but never fired - fixed here.
//  - "dismissed forever" (localStorage): set ONLY via the drawer's explicit,
//    unchecked-by-default "don't show this automatically next time"
//    checkbox - never inferred from a plain close/click-away. This is what
//    actually survives across sessions.
// Both wrap storage access in try/catch, mirroring shell/TraceProvider.tsx's
// FirstUseHint precedent, failing toward "already seen"/"already dismissed"
// rather than nagging if storage is unavailable.

const SEEN_PREFIX = "il-role-guide-seen-";
const DISMISSED_PREFIX = "il-role-guide-dismissed-";

export function hasSeenGuide(profileId: string): boolean {
  try {
    return sessionStorage.getItem(SEEN_PREFIX + profileId) === "1";
  } catch {
    return true;
  }
}

export function markGuideSeen(profileId: string): void {
  try {
    sessionStorage.setItem(SEEN_PREFIX + profileId, "1");
  } catch {
    /* non-fatal */
  }
}

/** Set only via the drawer's explicit, unchecked-by-default "don't show
 * this automatically next time" checkbox - never inferred from a plain
 * close/click-away. */
export function isGuideDismissedForever(profileId: string): boolean {
  try {
    return localStorage.getItem(DISMISSED_PREFIX + profileId) === "1";
  } catch {
    return true;
  }
}

export function setGuideDismissedForever(profileId: string, value: boolean): void {
  try {
    if (value) localStorage.setItem(DISMISSED_PREFIX + profileId, "1");
    else localStorage.removeItem(DISMISSED_PREFIX + profileId);
  } catch {
    /* non-fatal */
  }
}

/** Clears every "seen this session" flag - called from authStore.logout()
 * so a fresh login always gets one fresh auto-open. sessionStorage alone
 * isn't enough: it's scoped to the browser tab, not the login session, so
 * a same-tab logout+login (the normal way to test this) silently inherits
 * the previous login's "already seen" flag otherwise. Deliberately doesn't
 * touch isGuideDismissedForever's localStorage - that's the actual,
 * durable opt-out signal and must survive a logout. */
export function clearAllSeenGuides(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(SEEN_PREFIX)) keys.push(key);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* non-fatal */
  }
}
